import { useEffect, useRef, useSyncExternalStore } from 'react';

/**
 * A tiny registry of "this screen has edits it has not saved".
 *
 * The app has no global save bar: each page owns its own (see ui-patterns §7).
 * The update prompt still has to know when it may interrupt, and the forced
 * refresh dialog has to offer Save and refresh / Discard, so a screen with a
 * save bar reports itself here with `useUnsavedChangesSource`.
 */
export interface UnsavedSource {
  dirty: boolean;
  /** Persist the edits. Reject to say it failed; the dialog then stays open. */
  save?: () => Promise<unknown> | unknown;
  discard?: () => void;
}

const sources = new Map<symbol, UnsavedSource>();
const listeners = new Set<() => void>();
let dirtyCount = 0;

const recount = () => {
  const next = Array.from(sources.values()).filter((source) => source.dirty).length;
  if (next !== dirtyCount) {
    dirtyCount = next;
    listeners.forEach((listener) => listener());
  }
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** True while any registered screen has unsaved edits. */
export const useHasUnsavedChanges = (): boolean =>
  useSyncExternalStore(
    subscribe,
    () => dirtyCount > 0,
    () => false,
  );

export const hasUnsavedChanges = (): boolean => dirtyCount > 0;

/** Report a screen's unsaved state. Safe to call on every render. */
export function useUnsavedChangesSource(source: UnsavedSource): void {
  const idRef = useRef<symbol | undefined>(undefined);
  if (!idRef.current) idRef.current = Symbol('unsaved-source');
  const latest = useRef(source);
  latest.current = source;

  useEffect(() => {
    const id = idRef.current as symbol;
    // The registry reads through `latest`, so save/discard are never stale.
    sources.set(id, {
      get dirty() {
        return latest.current.dirty;
      },
      save: () => latest.current.save?.(),
      discard: () => latest.current.discard?.(),
    });
    recount();
    return () => {
      sources.delete(id);
      recount();
    };
  }, []);

  useEffect(() => {
    recount();
  }, [source.dirty]);
}

/** Save every dirty screen. Rejects on the first failure. */
export async function saveAllUnsaved(): Promise<void> {
  for (const source of Array.from(sources.values())) {
    if (source.dirty && source.save) await source.save();
  }
}

export function discardAllUnsaved(): void {
  sources.forEach((source) => {
    if (source.dirty) source.discard?.();
  });
}

/** Test helper. */
export function resetUnsavedChanges(): void {
  sources.clear();
  recount();
}
